import { Moment } from 'moment'

import { currentContractVersionsWithinDateRange } from 'modules/contracts/lib/utils'
import type { ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import type { ContractVersionsPayrollPolicies } from 'modules/contracts/store/collections/contractVersionsPayrollPolicies'
import type { Employees } from 'modules/employees/store/collections/employees'
import type Employee from 'modules/employees/store/models/Employee'
import type Policy from 'modules/payroll_bundle/store/models/Policy'

/**
 * It returns a list of active employees for a specific policy.
 *
 * We consider an employee active when:
 *
 * - The employee has an active contract when the date range defined by the contract's start and end date must overlap with the specified period.
 *   As a contract can contain multiple versions, we need first to get versions that are effective on the specified period.
 * - The employee does not have a termination date before the specified period.
 */
export default function filterActiveEmployeesByPolicy({
  employees,
  policy,
  contractVersions,
  contractVersionsPayrollPolicies,
  period,
}: {
  employees: Employees | Employee[]
  policy: Policy
  contractVersions: ContractVersions
  contractVersionsPayrollPolicies: ContractVersionsPayrollPolicies
  period: { startsOn: Moment; endsOn: Moment }
}): Employee[] {
  const activeContractVersions = currentContractVersionsWithinDateRange(
    contractVersions,
    period.startsOn,
    period.endsOn
  )
  const activePolicyContracts = activeContractVersions.filter(
    (contract) =>
      contract.activeWithinDateRange(period.startsOn, period.endsOn) &&
      contractVersionsPayrollPolicies.find({ contract_version_id: contract.id })?.attributes
        .payroll_policy_id === policy.id
  )

  const policyEmployeeIdsWithActiveContract = new Set(
    activePolicyContracts.map((contract) => contract.attributes.employee_id)
  )

  const activeEmployees = employees.filter(
    (employee) =>
      policyEmployeeIdsWithActiveContract.has(employee.id) &&
      employee.activeWithinDateRange(period.startsOn, period.endsOn)
  )

  return activeEmployees
}
